/**
 * STORYBOARD UI - Panels Module
 * Handles the Panel Library section: panel library, split grid, panel CRUD, picker
 * (Panel Library is now embedded in the Storyboard tab in idea canvas)
 *
 * Extracted from ui.js as part of Phase 2 refactoring
 */

import {
  getStoryboard,
  setStoryboard,
  getActiveStoryboard,
  setActiveIdeaId,
  getActiveIdeaId,
  setSceneFrame,
  getPanelPool,
  hasPanelPool,
  clearPanelPool,
  removePanelFromPool,
  addPanelToPool,
  addPanelsToPool,
  getSplitSettings,
  setSplitSettings,
  getSourceImage,
  setSourceImage,
  duplicatePanelInPool,
  saveToDatabase
} from '../state.js';
import { splitStoryboardGrid } from '../api.js';
import { openPreview } from '../../../../js/shared/image-preview.js';
import { downloadImage } from '../../../../js/core/download.js';
import { Icons } from '../../../../js/shared/icons.js';
import { openPanelSelector } from './panel-selector.js';

// ============================================================================
// MODULE STATE
// ============================================================================

// Track split-in-progress to prevent double-split
let isSplitting = false;
let lastSplitTime = 0;
const SPLIT_DEBOUNCE_MS = 3000; // 3 second debounce

// Callback for rendering storyboard (set by main ui.js)
let renderStoryboardCallback = null;

/**
 * Set the callback for rendering storyboard
 * Called by main ui.js during initialization
 */
export function setRenderStoryboardCallback(callback) {
  renderStoryboardCallback = callback;
}

/**
 * Check if splitting is in progress
 */
export function isSplittingInProgress() {
  return isSplitting;
}

// ============================================================================
// PANEL MANAGER HANDLERS
// ============================================================================

/**
 * Assign panels to scenes - fills both START and END frames
 * Panel mapping for 2x3 grid (6 panels) with N scenes:
 * - Scene 1: Panel 1 (start), Panel 2 (end)
 * - Scene 2: Panel 3 (start), Panel 4 (end)
 * - Scene 3: Panel 5 (start), Panel 6 (end)
 */
export function assignPanelsToScenes(storyboard, panels) {
  if (!storyboard?.scenes || !panels?.length) return;

  storyboard.scenes.forEach((scene, sceneIndex) => {
    // Initialize frames if needed
    if (!scene.frames) {
      scene.frames = { start: null, end: null };
    }

    // Calculate panel indices for this scene
    // Each scene gets 2 panels: start and end
    const startPanelIndex = sceneIndex * 2;
    const endPanelIndex = sceneIndex * 2 + 1;

    // Assign start frame
    if (panels[startPanelIndex]) {
      scene.frames.start = {
        url: panels[startPanelIndex].dataUrl,
        generatedAt: new Date().toISOString(),
        source: 'pool'
      };
      console.log(`[Storyboard] Assigned panel ${startPanelIndex + 1} to scene ${scene.seq} START`);
    }

    // Assign end frame
    if (panels[endPanelIndex]) {
      scene.frames.end = {
        url: panels[endPanelIndex].dataUrl,
        generatedAt: new Date().toISOString(),
        source: 'pool'
      };
      console.log(`[Storyboard] Assigned panel ${endPanelIndex + 1} to scene ${scene.seq} END`);
    }
  });
}

/**
 * Auto-import panels when switching to PANELS/SCENES tab
 * CRITICAL: Never deletes existing panels, never overwrites scene frames
 * Only ADDS new panels to the pool
 */
export async function autoImportPanels(ideaId, imageUrl) {
  // Prevent running if manual split is in progress
  if (isSplitting) {
    console.log('[Storyboard] Split in progress, skipping auto-import');
    return;
  }

  isSplitting = true;
  lastSplitTime = Date.now();
  console.log(`[Storyboard] Auto-importing panels for ${ideaId}`);

  // Show loading indicator in panel grid if visible
  const gridContainer = document.getElementById('canvas-panel-library-grid')
    || document.getElementById('panel-library-grid');
  if (gridContainer) {
    gridContainer.innerHTML = `
      <div class="panel-library-empty">
        <div class="loading-spinner"></div>
        <p>Analyzing storyboard image...</p>
      </div>
    `;
  }

  try {
    // Get split settings from state (default: 3 rows x 2 cols for vertical layout)
    const settings = getSplitSettings(ideaId);
    const rows = settings.rows || 3;
    const cols = settings.cols || 2;

    console.log(`[Storyboard] Using grid: ${rows}x${cols}`);
    const panels = await splitStoryboardGrid(imageUrl, rows, cols);

    // CRITICAL: NEVER delete existing panels - only ADD new ones
    const existingPanels = getPanelPool(ideaId);
    addPanelsToPool(ideaId, panels, false);

    // Ensure this idea is set as active
    setActiveIdeaId(ideaId);

    // CRITICAL: Only auto-assign if NO scenes have existing frame assignments
    const storyboard = getStoryboard(ideaId);
    const hasExistingFrames = storyboard?.scenes?.some(scene =>
      scene.frames?.start?.url || scene.frames?.end?.url
    );

    if (storyboard?.scenes && !hasExistingFrames) {
      assignPanelsToScenes(storyboard, panels);
      setStoryboard(ideaId, storyboard);
      console.log(`[Storyboard] Auto-assigned panels (no existing frames)`);
    } else {
      console.log(`[Storyboard] Preserved existing scene frame assignments`);
    }

    // Re-render BOTH views to ensure they are up to date
    if (renderStoryboardCallback) {
      renderStoryboardCallback(ideaId);
    }
    renderPanelsView();

    console.log(`[Storyboard] Auto-imported ${panels.length} panels (was ${existingPanels.length})`);

  } catch (error) {
    console.error('[Storyboard] Auto-import failed:', error);
    // Show error state in grid
    if (gridContainer) {
      gridContainer.innerHTML = `
        <div class="panel-library-empty">
          <div class="empty-icon">⚠</div>
          <p>Failed to auto-split image</p>
          <p style="font-size: 10px; opacity: 0.7;">Use manual SPLIT button above</p>
        </div>
      `;
    }
  } finally {
    isSplitting = false;
  }
}

/**
 * Handle importing panels from storyboard grid image (manual button click)
 * CRITICAL: Never deletes existing panels, never overwrites scene frames
 */
export async function handleImportPanels(button) {
  const ideaId = button.dataset.idea;
  const imageUrl = window.getIdeaImageUrl?.(ideaId);

  if (!imageUrl) {
    alert('No storyboard image found. Generate one first.');
    return;
  }

  // Update button state
  const originalHtml = button.innerHTML;
  button.disabled = true;
  button.innerHTML = '<span class="loading-spinner small"></span> SPLITTING...';

  try {
    // Get split settings from state (default: 3 rows x 2 cols for vertical layout)
    const settings = getSplitSettings(ideaId);
    const rows = settings.rows || 3;
    const cols = settings.cols || 2;

    console.log(`[Storyboard] Manual import using grid: ${rows}x${cols}`);
    const panels = await splitStoryboardGrid(imageUrl, rows, cols);

    // CRITICAL: NEVER delete existing panels - user is in full control
    // Just ADD new panels to the pool
    const existingPanels = getPanelPool(ideaId);
    addPanelsToPool(ideaId, panels, false);

    // Ensure this idea is set as active
    setActiveIdeaId(ideaId);

    // CRITICAL: Only auto-assign if NO scenes have existing frame assignments
    const storyboard = getStoryboard(ideaId);
    const hasExistingFrames = storyboard?.scenes?.some(scene =>
      scene.frames?.start?.url || scene.frames?.end?.url
    );

    if (storyboard?.scenes && !hasExistingFrames) {
      assignPanelsToScenes(storyboard, panels);
      setStoryboard(ideaId, storyboard);
      console.log(`[Storyboard] Auto-assigned panels to scenes (no existing frames)`);
    } else {
      console.log(`[Storyboard] Preserved existing scene frame assignments`);
    }

    // Re-render
    if (renderStoryboardCallback) {
      renderStoryboardCallback(ideaId);
    }

    console.log(`[Storyboard] Added ${panels.length} panels (was ${existingPanels.length})`);

  } catch (error) {
    console.error('[Storyboard] Import failed:', error);
    alert(`Import failed: ${error.message}`);
    button.innerHTML = originalHtml;
    button.disabled = false;
  }
}

// ============================================================================
// PANEL PICKER
// ============================================================================

/**
 * Render panel picker modal
 */
function renderPanelPicker(sceneSeq, frameType) {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  const panels = getPanelPool(ideaId);

  if (!panels.length) return '';

  const gridCols = panels.length <= 4 ? 2 : 3;

  return `
    <div class="panel-picker-overlay" id="panel-picker">
      <div class="panel-picker-modal">
        <div class="panel-picker-header">
          <h3>SELECT PANEL FOR SCENE ${sceneSeq}</h3>
          <button type="button" class="btn-close-picker" data-action="close-picker">✕</button>
        </div>
        <div class="panel-picker-grid cols-${gridCols}">
          ${panels.map((panel, i) => `
            <div class="panel-picker-item"
                 data-action="assign-panel"
                 data-panel-index="${i}"
                 data-scene="${sceneSeq}"
                 data-type="${frameType}">
              <img src="${panel.dataUrl}" alt="Panel ${i + 1}">
              <span class="panel-number">${panel.index}</span>
            </div>
          `).join('')}
        </div>
        <p class="panel-picker-tip">Click a panel to assign it to Scene ${sceneSeq}</p>
      </div>
    </div>
  `;
}

/**
 * Show panel picker modal
 */
export function showPanelPicker(sceneSeq, frameType) {
  // Remove existing picker if any
  closePanelPicker();

  // Add to body
  const html = renderPanelPicker(sceneSeq, frameType);
  if (html) {
    document.body.insertAdjacentHTML('beforeend', html);

    const overlay = document.getElementById('panel-picker');
    if (!overlay) return;

    // Add click outside to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closePanelPicker();
      }
    });

    // Add click handler for close button
    const closeBtn = overlay.querySelector('.btn-close-picker');
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      closePanelPicker();
    });

    // Add click handlers for panel items
    const panelItems = overlay.querySelectorAll('.panel-picker-item');
    panelItems.forEach(item => {
      item.addEventListener('click', async (e) => {
        e.stopPropagation();
        const panelIndex = parseInt(item.dataset.panelIndex);
        const scene = parseInt(item.dataset.scene);
        const type = item.dataset.type;

        const storyboard = getActiveStoryboard();
        const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
        const panels = getPanelPool(ideaId);

        if (panels[panelIndex]) {
          setSceneFrame(scene, type, panels[panelIndex].dataUrl, 'pool');
          closePanelPicker();
          if (renderStoryboardCallback) {
            renderStoryboardCallback(ideaId);
          }
          console.log(`[Storyboard] Assigned panel ${panelIndex + 1} to scene ${scene} ${type}`);

          // Persist frame assignment to database
          const saved = await saveToDatabase(ideaId);
          console.log(`[Storyboard] Frame assignment ${saved ? 'saved' : 'not saved (local only)'}`);
        }
      });
    });
  }
}

/**
 * Close panel picker modal
 */
export function closePanelPicker() {
  document.getElementById('panel-picker')?.remove();
}

/**
 * Handle assigning a panel from the pool to a scene frame
 */
export async function handleAssignPanel(target) {
  const panelIndex = parseInt(target.dataset.panelIndex);
  const sceneSeq = parseInt(target.dataset.scene);
  const frameType = target.dataset.type;

  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  const panels = getPanelPool(ideaId);

  if (panels[panelIndex]) {
    setSceneFrame(sceneSeq, frameType, panels[panelIndex].dataUrl, 'pool');
    closePanelPicker();
    if (renderStoryboardCallback) {
      renderStoryboardCallback(ideaId);
    }
    console.log(`[Storyboard] Assigned panel ${panelIndex + 1} to scene ${sceneSeq} ${frameType}`);

    // Persist frame assignment to database
    const saved = await saveToDatabase(ideaId);
    console.log(`[Storyboard] Frame assignment ${saved ? 'saved' : 'not saved (local only)'}`);
  }
}

// ============================================================================
// PANELS VIEW (Panel Library)
// ============================================================================

/**
 * Render the Panels view (Panel Library)
 * FIXED: Gets ideaId directly from state, not from storyboard object
 * This ensures panels display even when no video scenes have been generated yet
 * v96: Auto-imports panels if source image exists but pool is empty
 */
export function renderPanelsView() {
  // FIXED: Get ideaId directly from state - panels can exist without storyboard/scenes
  const ideaId = getActiveIdeaId();
  const storyboard = getActiveStoryboard();  // May be null if no scenes generated yet

  // Get source image URL
  const sourceImageUrl = ideaId ? window.getIdeaImageUrl?.(ideaId) : null;

  // Check if we need to auto-import (user visited PANELS before SCENES)
  const hasPanels = hasPanelPool(ideaId);

  if (sourceImageUrl && ideaId && !hasPanels && !isSplitting) {
    // Image exists but no panels - trigger auto-import
    console.log('[Panels] No panels found, triggering auto-import from image');
    autoImportPanels(ideaId, sourceImageUrl);
    // autoImportPanels will call renderPanelsView() again when done
    // Show loading state while splitting
    showPanelSplittingState();
    return;
  }

  // Get current panels and settings
  const panels = ideaId ? getPanelPool(ideaId) : [];
  const settings = ideaId ? getSplitSettings(ideaId) : { rows: 3, cols: 2 };

  // Render source section
  renderPanelSourceSection(sourceImageUrl, settings, ideaId);

  // Render panel grid
  renderPanelLibraryGrid(panels, storyboard);

  // Render actions
  renderPanelLibraryActions(panels.length > 0);
}

/**
 * Show loading state while panels are being split
 */
function showPanelSplittingState() {
  const gridContainer = document.getElementById('canvas-panel-library-grid')
    || document.getElementById('panel-library-grid');
  if (gridContainer) {
    gridContainer.innerHTML = `
      <div class="panel-library-empty">
        <div class="loading-spinner"></div>
        <p>Analyzing storyboard image...</p>
      </div>
    `;
  }
}

/**
 * Render the source image section with split controls
 * Supports both canvas (inline) and modal container IDs
 */
function renderPanelSourceSection(imageUrl, settings, ideaId) {
  // Try canvas ID first, then modal ID
  const container = document.getElementById('canvas-panel-source-section')
    || document.getElementById('panel-source-section');
  if (!container) return;

  const hasImage = !!imageUrl;
  const hasPanels = ideaId ? hasPanelPool(ideaId) : false;

  container.innerHTML = `
    <div class="source-preview">
      ${hasImage
        ? `<img src="${imageUrl}" alt="Source storyboard grid" />`
        : `<div class="source-preview-placeholder">
             <span class="icon">⬚</span>
             <span>No source image</span>
             <span>Generate a storyboard first</span>
           </div>`
      }
    </div>
    <div class="split-controls">
      <h4 class="split-controls-title">GRID SPLIT SETTINGS</h4>
      <div class="split-controls-row">
        <div class="split-control-group">
          <label for="split-rows">ROWS</label>
          <select id="split-rows" ${!hasImage ? 'disabled' : ''}>
            <option value="2" ${settings.rows === 2 ? 'selected' : ''}>2</option>
            <option value="3" ${settings.rows === 3 ? 'selected' : ''}>3</option>
            <option value="4" ${settings.rows === 4 ? 'selected' : ''}>4</option>
          </select>
        </div>
        <div class="split-control-group">
          <label for="split-cols">COLS</label>
          <select id="split-cols" ${!hasImage ? 'disabled' : ''}>
            <option value="2" ${settings.cols === 2 ? 'selected' : ''}>2</option>
            <option value="3" ${settings.cols === 3 ? 'selected' : ''}>3</option>
            <option value="4" ${settings.cols === 4 ? 'selected' : ''}>4</option>
          </select>
        </div>
        <button class="btn-split" data-action="split-grid" ${!hasImage ? 'disabled' : ''}>
          SPLIT
        </button>
        <button class="btn-select" data-action="select-source" data-url="${imageUrl || ''}" ${!hasImage ? 'disabled' : ''}>
          SELECT
        </button>
      </div>
      <div class="split-options-row">
        <label class="split-option-label">
          <input type="checkbox" id="split-trim" checked ${!hasImage ? 'disabled' : ''}>
          <span>Trim Borders</span>
        </label>
        <label class="split-option-label">
          <input type="checkbox" id="split-refine" ${!hasImage ? 'disabled' : ''}>
          <span>Edge Snap</span>
        </label>
      </div>
      <p class="split-status">
        ${hasPanels
          ? (() => {
              const panels = getPanelPool(ideaId);
              const splits = panels.filter(p => p.source === 'split').length;
              const uploads = panels.filter(p => p.source === 'upload').length;
              return `${panels.length} panels (${splits} splits, ${uploads} uploads)`;
            })()
          : hasImage
            ? 'Click SPLIT to extract panels from grid'
            : 'Generate a storyboard image first'
        }
      </p>
    </div>
  `;
}

/**
 * Render the panel library grid
 * Supports both canvas (inline) and modal container IDs
 */
function renderPanelLibraryGrid(panels, storyboard) {
  // Try canvas ID first, then modal ID
  const container = document.getElementById('canvas-panel-library-grid')
    || document.getElementById('panel-library-grid');
  if (!container) return;

  if (panels.length === 0) {
    container.innerHTML = `
      <div class="panel-library-empty">
        <div class="empty-icon">⬚</div>
        <p>No panels yet. Split a storyboard grid or upload images.</p>
      </div>
    `;
    return;
  }

  // Build assignment map (which panel is assigned where)
  const assignmentMap = buildPanelAssignmentMap(storyboard, panels);

  container.innerHTML = panels.map((panel, index) => {
    const assignment = assignmentMap[index];
    const isUpload = panel.source === 'upload';
    const isEdited = panel.source === 'edited';

    return `
      <div class="panel-library-item ${isUpload ? 'is-upload' : ''} ${isEdited ? 'is-edited' : ''}" data-panel-index="${index}">
        <img src="${panel.dataUrl}" alt="Panel ${index + 1}" />
        <span class="panel-index">${index + 1}</span>
        ${isUpload ? '<span class="panel-upload-badge">UPLOAD</span>' : ''}
        ${isEdited ? '<span class="panel-source-badge">EDITED</span>' : ''}
        ${assignment ? `<span class="panel-assignment">${assignment}</span>` : ''}
        <div class="panel-actions">
          <button class="btn-img-action" data-action="preview-panel" data-index="${index}" title="Preview">
            ${Icons.fullscreen16({ size: 14 })}
          </button>
          <button class="btn-img-action" data-action="edit-panel" data-index="${index}" title="Edit">
            ${Icons.editFilled({ size: 14 })}
          </button>
          <button class="btn-img-action" data-action="duplicate-panel" data-index="${index}" title="Duplicate">
            ${Icons.duplicate({ size: 14 })}
          </button>
          <button class="btn-img-action" data-action="download-panel" data-index="${index}" title="Download">
            ${Icons.download16({ size: 14 })}
          </button>
          <button class="btn-img-action" data-action="remove-panel" data-index="${index}" title="Remove">
            ${Icons.close({ size: 12 })}
          </button>
        </div>
      </div>
    `;
  }).join('');

  // Add the "Add Panel" slot
  container.innerHTML += `
    <label class="panel-library-add">
      <span class="add-icon">+</span>
      <span class="add-text">ADD PANEL</span>
      <span class="add-hint">or drag image here</span>
      <input type="file" accept="image/*" hidden data-action="upload-panel">
    </label>
  `;
}

/**
 * Handle panel download using centralized utility with R2 proxy support
 */
async function handleDownloadPanel(panelIndex) {
  // Get ideaId directly from state
  const ideaId = getActiveIdeaId();
  const panels = getPanelPool(ideaId);
  const panel = panels[panelIndex];

  if (!panel?.dataUrl) {
    console.warn('[Panels] No panel data to download');
    return;
  }

  // Generate filename
  const idSlice = ideaId ? ideaId.slice(0, 8) : 'panel';
  const sourceType = panel.source === 'upload' ? 'upload' : 'split';
  const filename = `panel-${panelIndex + 1}-${sourceType}-${idSlice}`;

  // Use centralized download utility (handles R2 proxy automatically)
  await downloadImage(panel.dataUrl, filename);
  console.log(`[Panels] Downloaded panel ${panelIndex + 1}`);
}

/**
 * Build a map of panel index -> assignment string (e.g., "Scene 1 START")
 */
function buildPanelAssignmentMap(storyboard, panels) {
  const map = {};

  if (!storyboard?.scenes) return map;

  storyboard.scenes.forEach((scene, sceneIndex) => {
    if (scene.frames?.start?.url) {
      // Find which panel this URL matches
      panels.forEach((panel, panelIndex) => {
        if (panel.dataUrl === scene.frames.start.url) {
          map[panelIndex] = `S${scene.seq} START`;
        }
      });
    }
    if (scene.frames?.end?.url) {
      panels.forEach((panel, panelIndex) => {
        if (panel.dataUrl === scene.frames.end.url) {
          map[panelIndex] = `S${scene.seq} END`;
        }
      });
    }
  });

  return map;
}

/**
 * Render panel library actions
 * Supports both canvas (inline) and modal container IDs
 */
function renderPanelLibraryActions(hasPanels) {
  // Try canvas ID first, then modal ID
  const container = document.getElementById('canvas-panel-library-actions')
    || document.getElementById('panel-library-actions');
  if (!container) return;

  container.innerHTML = `
    ${hasPanels ? `
      <button class="btn-clear-panels" data-action="clear-panels">CLEAR ALL PANELS</button>
    ` : ''}
  `;
}

/**
 * Handle click events in panels view
 */
export function handlePanelsViewClick(e) {
  const target = e.target.closest('[data-action]');
  if (!target) return;

  e.preventDefault();

  const action = target.dataset.action;

  switch (action) {
    case 'split-grid':
      handleSplitGrid();
      break;

    case 'remove-panel':
      handleRemovePanel(parseInt(target.dataset.index));
      break;

    case 'download-panel':
      handleDownloadPanel(parseInt(target.dataset.index));
      break;

    case 'duplicate-panel':
      handleDuplicatePanel(parseInt(target.dataset.index));
      break;

    case 'edit-panel':
      handleEditPanel(parseInt(target.dataset.index));
      break;

    case 'preview-panel':
      handlePreviewPanel(parseInt(target.dataset.index));
      break;

    case 'select-source':
      handleSelectSource(target.dataset.url);
      break;

    case 'clear-panels':
      handleClearPanels();
      break;
  }
}

/**
 * Handle change events in panels view (file uploads, select changes)
 */
export function handlePanelsViewChange(e) {
  const target = e.target;

  // File upload for adding panel
  if (target.dataset.action === 'upload-panel') {
    const file = target.files?.[0];
    if (file) {
      handlePanelUpload(file);
      target.value = ''; // Reset for re-upload
    }
    return;
  }

  // Split settings changes
  if (target.id === 'split-rows' || target.id === 'split-cols') {
    const storyboard = getActiveStoryboard();
    const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
    if (!ideaId) return;

    const rows = parseInt(document.getElementById('split-rows')?.value) || 2;
    const cols = parseInt(document.getElementById('split-cols')?.value) || 3;
    setSplitSettings(ideaId, rows, cols);
  }
}

/**
 * Handle split grid action
 * Only triggered by explicit user click on SPLIT button in Storyboard tab (panels section)
 */
async function handleSplitGrid() {
  // DEBUG: Find out who's calling this
  console.log('[Panels] handleSplitGrid called, isSplitting:', isSplitting, 'lastSplitTime:', lastSplitTime);
  console.trace('[Panels] Call stack:');

  // Guard 1: Check if panels section is actually visible
  // This prevents ghost clicks from hidden elements
  // Check BOTH generator modal structure (#tab-panels) AND idea canvas structure (.canvas-tab-content[data-tab="storyboard"])
  const modalPanelsTab = document.getElementById('tab-panels');
  const canvasStoryboardTab = document.querySelector('.canvas-tab-content[data-tab="storyboard"]');
  const isPanelsTabActive = modalPanelsTab?.classList.contains('active') || canvasStoryboardTab?.classList.contains('active');

  if (!isPanelsTabActive) {
    console.log('[Panels] Storyboard tab not active - ignoring split request');
    return;
  }

  // Guard 2: Prevent double-split (race condition guard)
  if (isSplitting) {
    console.log('[Panels] Split already in progress, ignoring');
    return;
  }

  // Guard 3: Time-based debounce to prevent rapid double-splits
  const now = Date.now();
  if (now - lastSplitTime < SPLIT_DEBOUNCE_MS) {
    console.log('[Panels] Split debounced - too soon after last split');
    return;
  }

  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  if (!ideaId) return;

  // Guard 4: Check if panels already exist with SAME source image
  // Skip if we already have split panels from the current image
  const currentPanels = getPanelPool(ideaId);
  const splitPanelCount = currentPanels.filter(p => p.source === 'split').length;
  if (splitPanelCount > 0) {
    const sourceImage = getSourceImage(ideaId);
    const currentImageUrl = window.getIdeaImageUrl?.(ideaId);
    if (sourceImage === currentImageUrl) {
      console.log('[Panels] Already have', splitPanelCount, 'panels from this image - use RE-SPLIT button if intentional');
      // Don't block completely - user may want to re-split with different settings
      // But DO update button text to indicate this
      const splitBtn = document.querySelector('[data-action="split-grid"]');
      if (splitBtn && splitBtn.textContent === 'SPLIT') {
        splitBtn.textContent = 'RE-SPLIT';
      }
    }
  }

  const imageUrl = window.getIdeaImageUrl?.(ideaId);
  if (!imageUrl) {
    alert('No storyboard image found. Generate one first.');
    return;
  }

  isSplitting = true;
  lastSplitTime = now;

  // Get settings
  const settings = getSplitSettings(ideaId);
  const rows = parseInt(document.getElementById('split-rows')?.value) || settings.rows;
  const cols = parseInt(document.getElementById('split-cols')?.value) || settings.cols;

  // Get split options from checkboxes
  const trimBorder = document.getElementById('split-trim')?.checked ?? true;
  const refineEdges = document.getElementById('split-refine')?.checked ?? false;

  // Update button state
  const splitBtn = document.querySelector('[data-action="split-grid"]');
  if (splitBtn) {
    splitBtn.disabled = true;
    splitBtn.classList.add('loading');
    splitBtn.textContent = 'SPLITTING...';
  }

  try {
    // Split the grid with options
    const newPanels = await splitStoryboardGrid(imageUrl, rows, cols, { trimBorder, refineEdges });

    // Add source metadata to split panels
    const splitPanels = newPanels.map((panel, i) => ({
      ...panel,
      source: 'split',
      splitAt: new Date().toISOString()
    }));

    // Store settings first
    setSplitSettings(ideaId, rows, cols);
    setSourceImage(ideaId, imageUrl);

    // CRITICAL: NEVER delete existing panels - user is in full control
    // SPLIT only ADDS new panels to the pool
    // User can manually delete old panels if they want
    const existingPanels = getPanelPool(ideaId);
    const mergedPanels = addPanelsToPool(ideaId, splitPanels, false);

    // CRITICAL: Only auto-assign if NO scenes have existing frame assignments
    // This prevents overwriting user's custom frame selections
    setActiveIdeaId(ideaId);
    const hasExistingFrames = storyboard?.scenes?.some(scene =>
      scene.frames?.start?.url || scene.frames?.end?.url
    );

    if (!hasExistingFrames) {
      // First time split - auto-assign panels to scenes
      assignPanelsToScenes(storyboard, splitPanels);
      setStoryboard(ideaId, storyboard);
      console.log(`[Panels] Auto-assigned ${splitPanels.length} panels to scenes (no existing frames)`);
    } else {
      // Existing frames: Don't touch scene frame assignments
      // User can manually reassign from the panel library
      console.log(`[Panels] Preserved existing scene frame assignments`);
    }

    // Re-render panels view
    renderPanelsView();

    console.log(`[Panels] Added ${splitPanels.length} new panels (${rows}x${cols}), total now: ${mergedPanels.length} (was ${existingPanels.length})`);

  } catch (error) {
    console.error('[Panels] Split failed:', error);
    alert(`Split failed: ${error.message}`);
  } finally {
    isSplitting = false;
    if (splitBtn) {
      splitBtn.disabled = false;
      splitBtn.classList.remove('loading');
      splitBtn.textContent = 'RE-SPLIT';
    }
  }
}

/**
 * Handle remove panel action
 */
function handleRemovePanel(panelIndex) {
  // FIXED: Get ideaId directly from state
  const ideaId = getActiveIdeaId();
  const storyboard = getActiveStoryboard();
  if (!ideaId) return;

  // Get the panel being removed
  const panels = getPanelPool(ideaId);
  const panel = panels[panelIndex];
  if (!panel) return;

  // Clear any scene frames that use this panel
  if (storyboard?.scenes) {
    storyboard.scenes.forEach(scene => {
      if (scene.frames?.start?.url === panel.dataUrl) {
        scene.frames.start = null;
      }
      if (scene.frames?.end?.url === panel.dataUrl) {
        scene.frames.end = null;
      }
    });
    setStoryboard(ideaId, storyboard);
  }

  // Remove from pool
  removePanelFromPool(ideaId, panelIndex);

  // Re-render
  renderPanelsView();
}

/**
 * Duplicate a panel in the library
 * Creates a copy of the panel at the specified index
 * @param {number} panelIndex - 0-based index of the panel to duplicate
 */
function handleDuplicatePanel(panelIndex) {
  // FIXED: Get ideaId directly from state
  const ideaId = getActiveIdeaId();
  if (!ideaId) return;

  // Call state function to duplicate
  const newPanel = duplicatePanelInPool(ideaId, panelIndex);

  if (newPanel) {
    // Re-render to show the new panel
    renderPanelsView();
    console.log(`[Storyboard UI] Duplicated panel ${panelIndex + 1}`);
  }
}

/**
 * Preview a panel in the image preview modal
 * @param {number} panelIndex - 0-based index of the panel to preview
 */
function handlePreviewPanel(panelIndex) {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  if (!ideaId) return;

  const panels = getPanelPool(ideaId);
  if (!panels || panels.length === 0) {
    console.warn('[Storyboard] No panels to preview');
    return;
  }

  // Build preview array from all panels
  const previewImages = panels.map((panel, i) => ({
    url: panel.dataUrl,
    caption: `Panel ${i + 1}${panel.source === 'upload' ? ' (Upload)' : ''}`,
    alt: `Panel ${i + 1}`
  }));

  // Open preview at the selected panel index
  openPreview(previewImages, panelIndex);
  console.log(`[Storyboard] Opened preview for panel ${panelIndex + 1}`);
}

/**
 * Open panel selector to extract a region from the SOURCE storyboard image
 * @param {string} sourceUrl - URL of the source storyboard image
 */
function handleSelectSource(sourceUrl) {
  const ideaId = getActiveIdeaId();
  if (!ideaId) {
    console.warn('[Panels] No active idea for selection');
    return;
  }

  // Get source URL from state if not provided
  if (!sourceUrl) {
    sourceUrl = getSourceImage(ideaId);
  }

  if (!sourceUrl) {
    console.warn('[Panels] No source image URL available');
    alert('No source storyboard image. Generate a storyboard image first.');
    return;
  }

  // Validate it's a real remote URL (not base64 data URL)
  if (!sourceUrl.startsWith('http://') && !sourceUrl.startsWith('https://')) {
    console.warn('[Panels] Source has non-HTTP URL:', sourceUrl?.substring(0, 100));
    alert('Cannot select from this source - it has a local/base64 image.');
    return;
  }

  // Open the panel selector modal with source image
  openPanelSelector(sourceUrl, -1, ideaId);  // -1 indicates source image, not a panel
  console.log('[Panels] Opened selector for source storyboard image');
}

/**
 * Edit a panel using contextual image editing
 * Opens the image edit modal with panel context
 * @param {number} panelIndex - 0-based index of the panel to edit
 */
async function handleEditPanel(panelIndex) {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  if (!ideaId) return;

  // Get the panel to verify it exists
  const panels = getPanelPool(ideaId);
  if (!panels || !panels[panelIndex]) {
    console.warn('[Storyboard] Cannot edit: panel not found at index', panelIndex);
    return;
  }

  const panel = panels[panelIndex];
  const imageUrl = panel.dataUrl || panel.url;

  console.log('[Storyboard] Edit panel debug:', {
    panelIndex,
    hasDataUrl: !!panel.dataUrl,
    hasUrl: !!panel.url,
    imageUrl: imageUrl?.substring(0, 80),
    source: panel.source
  });

  if (!imageUrl) {
    alert('No image in this panel to edit.');
    return;
  }

  // Validate it's a real remote URL (not base64 data URL)
  if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
    console.warn('[Storyboard] Panel has non-HTTP URL:', imageUrl?.substring(0, 100));
    alert('Cannot edit this panel - it has a local/base64 image.\n\nSplit panels should have R2 URLs. Try re-splitting the grid.');
    return;
  }

  // Open edit modal with panel context
  // Note: Overlay is NOT shown here - it's shown when user actually submits the edit
  if (typeof window.openEditModal === 'function') {
    await window.openEditModal(ideaId, {
      type: 'panel',
      panelIndex: panelIndex
    });
    console.log(`[Storyboard] Opened edit modal for Panel ${panelIndex + 1}`);
  } else {
    console.error('[Storyboard] Image Edit module not loaded');
    alert('Edit feature not available. Please refresh the page.');
  }
}

/**
 * Show editing overlay on a panel
 * @param {number} panelIndex
 */
export function showPanelEditingOverlay(panelIndex) {
  const panelEl = document.querySelector(`.panel-library-item[data-panel-index="${panelIndex}"]`);
  if (!panelEl) return;

  // Add editing class
  panelEl.classList.add('editing');

  // Add overlay if not already present
  if (!panelEl.querySelector('.panel-editing-overlay')) {
    panelEl.insertAdjacentHTML('beforeend', `
      <div class="panel-editing-overlay">
        <div class="pixel-loader"></div>
        <span class="editing-text">EDITING...</span>
      </div>
    `);
  }
}

/**
 * Hide editing overlay on a panel
 * @param {number} panelIndex
 */
export function hidePanelEditingOverlay(panelIndex) {
  const panelEl = document.querySelector(`.panel-library-item[data-panel-index="${panelIndex}"]`);
  if (!panelEl) return;

  panelEl.classList.remove('editing');
  const overlay = panelEl.querySelector('.panel-editing-overlay');
  if (overlay) overlay.remove();
}

/**
 * Handle clear all panels
 */
function handleClearPanels() {
  if (!confirm('Clear all panels? This will also clear all scene frames.')) return;

  // FIXED: Get ideaId directly from state
  const ideaId = getActiveIdeaId();
  const storyboard = getActiveStoryboard();
  if (!ideaId) return;

  // Clear all scene frames
  if (storyboard?.scenes) {
    storyboard.scenes.forEach(scene => {
      scene.frames = { start: null, end: null };
    });
    setStoryboard(ideaId, storyboard);
  }

  // Clear panel pool
  clearPanelPool(ideaId);

  // Re-render
  renderPanelsView();
}

/**
 * Handle panel upload (add custom panel)
 * Uploads to R2 and adds URL to pool (not Base64)
 */
async function handlePanelUpload(file) {
  // FIXED: Get ideaId directly from state
  const ideaId = getActiveIdeaId();
  if (!ideaId) return;

  // Show uploading state on ADD PANEL slot
  const addSlot = document.querySelector('.panel-library-add');
  if (addSlot) {
    addSlot.classList.add('uploading');
    addSlot.innerHTML = `
      <div class="upload-spinner"></div>
      <span class="add-text">UPLOADING...</span>
      <span class="add-hint">${file.name}</span>
    `;
  }

  try {
    // Upload to R2 via backend
    const formData = new FormData();
    formData.append('image', file);

    const baseUrl = window.API_CONFIG?.baseUrl || '';
    const response = await fetch(`${baseUrl}/api/upload`, {
      method: 'POST',
      body: formData
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Upload failed');
    }

    // Add URL (not Base64) to pool
    addPanelToPool(ideaId, {
      dataUrl: data.url, // URL, not Base64
      source: 'upload',
      uploadedAt: new Date().toISOString()
    });

    // Re-render (this will reset the ADD PANEL slot)
    renderPanelsView();

    console.log(`[Storyboard] Panel uploaded to R2: ${data.url}`);

  } catch (error) {
    console.error('[Storyboard] Panel upload failed:', error);
    alert(`Upload failed: ${error.message}`);

    // Reset ADD PANEL slot on error
    if (addSlot) {
      addSlot.classList.remove('uploading');
      addSlot.innerHTML = `
        <span class="add-icon">+</span>
        <span class="add-text">ADD PANEL</span>
        <span class="add-hint">or drag image here</span>
        <input type="file" accept="image/*" hidden data-action="upload-panel">
      `;
    }
  }
}

// ============================================================================
// EXPORTS: Import panels to scenes utility
// ============================================================================

/**
 * Import existing panels from pool into scene frames (without re-splitting)
 * @param {string} ideaId - The idea ID
 * @returns {boolean} True if import succeeded
 */
export function importPanelsToScenes(ideaId) {
  const panels = getPanelPool(ideaId);
  if (!panels || panels.length === 0) {
    console.warn('[Storyboard] No panels in pool to import');
    return false;
  }

  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) {
    console.warn('[Storyboard] No storyboard/scenes to import into');
    return false;
  }

  // Assign panels to scenes
  assignPanelsToScenes(storyboard, panels);

  // Persist the updated storyboard
  setStoryboard(ideaId, storyboard);

  // Re-render to show images
  if (renderStoryboardCallback) {
    renderStoryboardCallback(ideaId);
  }

  console.log(`[Storyboard] Imported ${panels.length} panels into scenes`);
  return true;
}
